// domain/constants/modifierRule.js

/**
 * The vocabulary a payer's conditional modifier rule is written in.
 *
 * WHY A CONDITION RATHER THAN AN AXIS. An earlier draft of this bet asked which axis a payer reads,
 * the performer's degree or their credential. Real payer sheets do not answer that question: one
 * sheet routinely keys 97153 to the role, reads credential AND degree together on 97155, and gives
 * 97151 no modifier at all. The conditions seen in the field include credential alone, degree alone,
 * both required together, and either of two alternatives — and an axis cannot express the third.
 *
 * SO A RULE IS AN ORDERED LIST OF ROWS, each a condition over the performer and the modifiers it
 * produces, evaluated in order with the first match winning. Conditions within a row are joined with
 * AND; a second row is how a rule says OR. That is disjunctive normal form, which expresses any
 * condition a payer can write, while the editor still shows two dropdowns.
 *
 * THE LIST IS PER COVERED SERVICE, NOT PER PAYER, and that is the load-bearing choice. HM through HP
 * are education-level modifiers by their HCPCS definition, which payers borrowed as credential
 * tiers, so a sheet reads degree in one row and credential in the next. Keying the list to the
 * service is also what makes "never bill up" fall out for free: 97153 is billed by whoever worked as
 * the technician, so its list is a single row matching anyone, and a master's-holding BCBA on that
 * code bills HM rather than the tier their qualifications would otherwise earn.
 *
 * THE STORED SHAPE IS MORE GENERAL THAN THE SCREEN, deliberately. Firestore is expensive to
 * restructure and this record will grow: a row stores a LIST of conditions so place of service,
 * member age, session grouping and supervisor tier can arrive as new kinds of FACT rather than as a
 * schema change; a result stores a LIST of modifiers each tagged by ROLE so a state program wanting
 * a regional U-modifier and a credential modifier from one match is expressible; and an outcome is
 * TYPED so "hold" survives even though the editor reaches it only by falling off the list.
 */

/**
 * What a condition asks about. The first two kinds are the performer's; the record a rule is
 * evaluated against carries session, member and service-state facts for the kinds that follow.
 */
export const CONDITION_FACTS = {
  CREDENTIAL: 'credential',
  DEGREE_LEVEL: 'degreeLevel',
};

/**
 * How a condition compares.
 *
 * `atLeast` IS THE ONLY HONEST READING OF A DEGREE ROW. Payer sheets say "master's or higher" where
 * they are careful and "master's" where they are not, and they mean the same thing: a doctorate does
 * not disqualify somebody from a master's-level tier. Exact-match would hold a doctorate-holding
 * BCBA who matched no higher row, which is the overbilling direction's opposite but still wrong.
 */
export const CONDITION_COMPARISONS = {
  HOLDS: 'holds',
  AT_LEAST: 'atLeast',
};

/**
 * The comparison each kind of condition is asked with — the only pairings the vocabulary defines.
 *
 * NOT A FACT ABOUT THE SCREEN, though the editor is the reason it is written down. The projector
 * enforces the same two pairings and returns false for any other, so `credential atLeast` is not a
 * rule this platform means something different by — it is a rule nothing can evaluate. The editor
 * refusing to open one and the resolver refusing to match it are the same answer to the same fact.
 *
 * WHICH IS WHY A KNOWN FACT UNDER AN UNKNOWN COMPARISON IS AS UNREADABLE AS AN UNKNOWN FACT, and
 * `credential oneOf` is the one to picture: a later build could write it, today's single-select
 * would draw it as though it said `holds`, and the next selection would store that.
 */
export const AUTHORED_COMPARISONS = {
  [CONDITION_FACTS.CREDENTIAL]: CONDITION_COMPARISONS.HOLDS,
  [CONDITION_FACTS.DEGREE_LEVEL]: CONDITION_COMPARISONS.AT_LEAST,
};

/**
 * What a modifier DOES for a payer, which is what ordering is derived from.
 *
 * TAGGED RATHER THAN POSITIONAL, because a claim line's modifier order can itself be a rejection
 * reason and payers state the requirement in terms of meaning — "credential modifier first" — never
 * in terms of a literal. Tagging keeps the ordering rule free of any particular code, so nothing has
 * to know that HO is a credential modifier and U3 is a regional one.
 */
export const MODIFIER_ROLES = {
  RATE: 'rate',
  REGIONAL: 'regional',
  TELEHEALTH: 'telehealth',
  INFORMATIONAL: 'informational',
};

/**
 * The order roles take on a line, most rate-determining first.
 *
 * A CONVENTION, NOT A CLAIMS RULE, and the same one `codingResolver` already applies between a
 * payer's base set and its telehealth modifier. What determines the rate reads first and what
 * qualifies the encounter reads last. A payer stating an order that contradicts this needs an
 * override of its own; nothing here is a substitute for one.
 */
export const MODIFIER_ROLE_ORDER = [
  MODIFIER_ROLES.RATE,
  MODIFIER_ROLES.REGIONAL,
  MODIFIER_ROLES.INFORMATIONAL,
  MODIFIER_ROLES.TELEHEALTH,
];

/**
 * What a matched row produces.
 *
 * `HOLD` IS NOT AUTHORED BY THE EDITOR YET and is stored anyway. Falling off the bottom of the list
 * already holds, which covers "this performer meets no tier this payer defines". What it does not
 * cover is a payer that defines a tier explicitly in order to refuse it — paying nothing for a
 * technician on 97155 is a real sheet, and expressing it as a missing row would be indistinguishable
 * from nobody having finished the configuration.
 */
export const RULE_OUTCOMES = {
  BILL: 'bill',
  HOLD: 'hold',
};

/**
 * A credential condition's value: which body issued it and at what level, as one string.
 *
 * COMPOSITE RATHER THAN A BARE LEVEL, because a level means nothing without its issuer. No two
 * bodies share a level string today, and relying on that would make a future collision a silent
 * mismatch rather than a compile error — a payer's "BCBA" row quietly matching another body's.
 *
 * A STRING RATHER THAN A PAIR because it is a select's value and a map key as much as it is stored
 * data, and a structured value in either position buys nothing a split does not.
 */
export const credentialConditionValue = (organization, level) => `${organization}:${level}`;

/**
 * The body and level a credential condition names, or nulls for anything unparseable.
 *
 * Tolerant rather than throwing: this reads a schemaless document that an older client may have
 * written, and a condition nobody can parse must fail to match rather than take the payer's whole
 * rule set down.
 */
export function parseCredentialConditionValue(value) {
  if (typeof value !== 'string') {
    return { organization: null, level: null };
  }

  const separator = value.indexOf(':');

  if (separator <= 0 || separator === value.length - 1) {
    return { organization: null, level: null };
  }

  return { organization: value.slice(0, separator), level: value.slice(separator + 1) };
} // parseCredentialConditionValue()
